import React from 'react';
import type { ImageSourcePropType } from 'react-native';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../../../hooks';
import { useModalBottomSpacing } from '../../hooks/';
import { Modal } from '../../Modal';

/**
 * Modals / Generic / Modal-Dialogue according to figma's specifications.
 * @see {@link https://www.figma.com/file/8rLhbY9578ml8urgV2NCpb/Garnish---React-Library?node-id=2427%3A369&t=n7oDOuprW5aBUUXw-0 Figma | Modals}
 */

// ─────────────────────────────────────────────────────────────────────────────

export const ModalDialogue = (props: ModalDialogueContentProps) => {
  const {
    children,
    animationType = 'fade',
    size = 'medium',
    headline,
    subhead,
    body,
    imageSrc,
    palette,
    onRequestClose,
    ...modalProps
  } = props;

  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const shouldRenderCloseBtn = onRequestClose !== undefined;

  // ─── Styles ──────────────────────────────────────────────────────────

  const modalDialogBottomSpacing = useModalBottomSpacing();
  const modalDialogBottomSpacingStyle = useStyle(
    () => ({ paddingBottom: modalDialogBottomSpacing }),
    [modalDialogBottomSpacing],
  );
  const modalDialogStyles = [
    match([styles.modalDialogXS, styles.modalDialogSM]),
    modalDialogBottomSpacingStyle,
    shouldRenderCloseBtn
      ? match([styles.modalDialogWithCloseBtnXS, undefined])
      : undefined,
  ];
  const modalDialogInnerContentStyles = [
    styles.modalDialogInnerContent,
    shouldRenderCloseBtn
      ? match([undefined, styles.modalDialogInnerContentWithCloseBtnSM])
      : undefined,
  ];
  const modalCloseBtnStyles = match([
    styles.modalCloseBtnXS,
    styles.modalCloseBtnSM,
  ]);
  const modalImageStyles = match([styles.modalImageXS, styles.modalImageSM]);
  const modalDialogContentContainerStyles = match([
    undefined,
    styles.modalDialogContentContainerSM,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      animationType={animationType}
      size={size}
      palette={palette}
      onRequestClose={onRequestClose}
      {...modalProps}
    >
      {shouldRenderCloseBtn ? (
        <Modal.FloatingCloseBtn
          onPress={onRequestClose}
          palette={palette}
          style={modalCloseBtnStyles}
        />
      ) : null}

      <ScrollView
        style={modalDialogStyles}
        contentContainerStyle={modalDialogContentContainerStyles}
      >
        {imageSrc ? (
          <Modal.Image imageUrl={imageSrc} style={modalImageStyles} />
        ) : null}

        <View style={modalDialogInnerContentStyles}>
          {headline ? (
            <Modal.Headline palette={palette}>{headline}</Modal.Headline>
          ) : null}

          {subhead ? (
            <Modal.Subhead palette={palette}>{subhead}</Modal.Subhead>
          ) : null}

          {body ? (
            <Modal.BodyText palette={palette}>{body}</Modal.BodyText>
          ) : null}

          {children}
        </View>
      </ScrollView>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MODAL_CLOSE_BTN_CONTAINER_HEIGHT = theme.spacing.large;
const MODAL_DIALOG_SPACING_VERTICAL = theme.spacing.large;
const MODAL_DIALOG_SPACING_HORIZONTAL_XS = theme.spacing.medium;
const MODAL_DIALOG_SPACING_HORIZONTAL_SM = theme.spacing.large;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  modalDialogXS: {
    marginTop: MODAL_DIALOG_SPACING_VERTICAL,
  },
  modalDialogSM: {
    marginTop: MODAL_DIALOG_SPACING_VERTICAL,
  },
  modalDialogWithCloseBtnXS: {
    marginTop:
      MODAL_CLOSE_BTN_CONTAINER_HEIGHT +
      MODAL_DIALOG_SPACING_VERTICAL +
      theme.spacing.medium,
  },
  modalDialogContentContainerSM: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  modalCloseBtnXS: {
    margin: 0, // override default style
    marginTop: MODAL_DIALOG_SPACING_VERTICAL,
    marginRight: MODAL_DIALOG_SPACING_HORIZONTAL_XS,
  },
  modalCloseBtnSM: {
    margin: 0, // override default style
    marginTop: MODAL_DIALOG_SPACING_VERTICAL,
    marginRight: MODAL_DIALOG_SPACING_HORIZONTAL_SM,
  },
  modalDialogInnerContent: {
    flex: 1,
  },
  modalDialogInnerContentWithCloseBtnSM: {
    paddingTop: MODAL_CLOSE_BTN_CONTAINER_HEIGHT + theme.spacing.medium,
  },
  modalImageXS: {
    marginBottom: theme.spacing.large,
  },
  modalImageSM: {
    marginLeft: MODAL_DIALOG_SPACING_HORIZONTAL_SM,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ModalDialogueContentProps = {
  readonly headline?: string;
  readonly subhead?: string;
  readonly body?: string;
  readonly imageSrc?: ImageSourcePropType;
} & React.ComponentProps<typeof Modal>;
